<script lang="ts" setup>
import { ref } from 'vue'
import Popper from 'vue3-popper'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import {
  updateStateUser,
  getUsers,
  changePasswordUser
} from '@/app/core/services/users'
import { useField, useForm } from 'vee-validate'
import { object, string } from 'yup'
import {
  DBtn,
  DTextField,
  DToggleField,
  DTable,
  DModal,
  DLoading,
  SearchMdIcon,
  Edit03Icon,
  Lock03Icon,
  LockUnlocked03Icon,
  LockKeyholeSquareIcon,
  Lock02Icon,
  EyeIcon,
  EyeOffIcon
} from 'front-library'
import {
  generateReportList,
  getReportRequest
} from '@/app/core/services/reports'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const showModalChangePassword = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const acceptQuestion = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const search = ref()
const includeInactives = ref(false)
const userUpdateState = ref()
const urlFile = ref(null)
const refresh = ref()
const payload = ref()

/* Datos formulario */
const dataChangePassword = ref({
  userNewPassword: '',
  userConfirmNewPassword: ''
})
const showNewPassword = ref(true)
const showConfirmNewPassword = ref(true)
const dataUserSelect = ref<any>()
const wrongData = ref('')

/* Datos componente table */
const columns = ref([
  { name: 'nameUser', title: t('users.list.name'), table: 'name' },
  { name: 'state', title: t('users.list.status'), table: 'enabled' },
  { name: 'rol', title: t('users.list.roles') },
  { name: 'payroll', title: t('users.list.payrolls') },
  { name: 'actions', title: '' }
])
const rows = ref([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const tableUsers = ref(null)
const sortBy = ref('')

/* Redirect's */
const createUser = () => {
  void router.push({ name: 'CreateUser' })
}
const updateUser = (id: any) => {
  void router.push({ name: 'UpdateUser', params: { id } })
}
const getUser = (id: any) => {
  void router.push({ name: 'GetUser', params: { id } })
}

/* Cambiar estado del usuario */
const changeStateUser = (values: any) => {
  const text = values.enabled
    ? t('table.action.deactivate')
    : t('table.action.activate')
  userUpdateState.value = [
    { name: values.name, id: values.id, enabled: values.enabled }
  ]
  titleModal.value = t('users.list.actionStatus', {
    status: text,
    user: values.name as string
  })
  descriptionModal.value = ''
  typeAlertModal.value = 'question'
  acceptQuestion.value = t('modal.acceptQuestion', { action: text })
  showModal.value = true
}
const changeStateConfirm = async () => {
  loading.value = true
  messageLoading.value = ''
  showModal.value = false
  await updateStateUser(
    userUpdateState.value[0].id,
    !userUpdateState.value[0].enabled
  )
    .then(response => {
      notifications.addNotification({
        title: !userUpdateState.value[0].enabled
          ? t('notification.activated')
          : t('notification.disabled'),
        text: t('users.notification.changeStatus', {
          user: userUpdateState.value[0].name as string,
          status: !userUpdateState.value[0].enabled
            ? t('table.action.asset')
            : t('table.action.deactivated')
        }),
        type: 'success',
        time: 10000
      })
      void users()
    })
    .catch((error: any) => {
      notificationError(error, 'users', t, notifications, loading)
    })
}

/* Generar contraseña */
function getPassword() {
  const lowercase = 'abcdefghijklmnopqrstuvwxyz'
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const numbers = '0123456789'
  const specialCharacters = `;:.,_\-*!?¿¡/#$+|"<>'`
  const allCharacters = lowercase + uppercase + numbers + specialCharacters
  //
  function getRandomCharacter(string: any) {
    const index = Math.floor(Math.random() * string.length)
    return string.charAt(index)
  }
  //
  let password = ''
  password += getRandomCharacter(lowercase)
  password += getRandomCharacter(uppercase)
  password += getRandomCharacter(numbers)
  password += getRandomCharacter(specialCharacters)
  //
  for (let i = 4; i < Math.floor(Math.random() * (20 - 8 + 1)) + 8; i++) {
    password += getRandomCharacter(allCharacters)
  }
  password = password
    .split('')
    .sort(function () {
      return 0.5 - Math.random()
    })
    .join('')
  userNewPassword.value = password
  userConfirmNewPassword.value = password
}

/* Cambiar contraseña del usuario */
const changePassword = (user: any) => {
  dataUserSelect.value = user
  acceptQuestion.value = t('form.button.update')
  showModalChangePassword.value = true
}

// Esquema de validación campos obligatorios
const validationSchema = object().shape({
  userNewPassword: string()
    .required(t('form.valid.required'))
    .matches(
      /^(?=(?:.*\d))(?=.*[A-Z])(?=.*[a-z])(?=.*[;:.,_\-*!?¿¡/#$%&+|°"<>'])\S{8,20}$/g,
      t('form.valid.otherPassword')
    ),
  userConfirmNewPassword: string().when(
    'userNewPassword',
    (userNewPassword, schema) => {
      if (errors.value.userNewPassword === undefined) {
        return schema
          .required(t('form.valid.required'))
          .equals(userNewPassword, t('form.valid.equalsPassword'))
      } else {
        return schema
          .required(t('form.valid.required'))
          .matches(
            /^(?=(?:.*\d))(?=.*[A-Z])(?=.*[a-z])(?=.*[;:.,_\-*!?¿¡/#$%&+|°"<>'])\S{8,20}$/g,
            t('form.valid.otherPassword')
          )
      }
    }
  )
})

// Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario
const { handleSubmit, errors, resetForm } = useForm({
  initialValues: dataChangePassword.value,
  validationSchema
})

// Definicion de campos del formulario
const { value: userNewPassword } = useField<string>('userNewPassword')
const { value: userConfirmNewPassword } = useField<string>(
  'userConfirmNewPassword'
)
const onSubmit = handleSubmit((values, actions) => {
  void updateDataUser(values)
})

const updateDataUser = async (values: any) => {
  try {
    closeModal()
    loading.value = true
    messageLoading.value = t('loading.updating')

    const result = await changePasswordUser(
      dataUserSelect.value.id,
      values.userNewPassword
    )
    if (result) {
      loading.value = false
      wrongData.value = ''
      resetForm({})
      notifications.addNotification({
        title: t('users.notification.changeSuccess'),
        text: t('users.notification.changePassword', {
          user: dataUserSelect.value.name
        }),
        type: 'success',
        time: 10000
      })
    }
  } catch (error: any) {
    notificationError(error, 'users', t, notifications, loading)
    /*switch (error.name) {
      case 'LimitExceededException':
        wrongData.value =
          'Se excedió el límite de intentos, intente después de 15 minutos.'
        break
      default:
        wrongData.value = error.name
        break
    }*/
  }
}

/* Generar reporte */
const downloadList = async () => {
  try {
    loading.value = true
    messageLoading.value = t('loading.report')
    urlFile.value = null
    const newColumns = columns.value
      .filter(item => item.title !== '')
      .map(item => (item.title === 'Nóminas' ? 'Nominas' : item.title))
    const index = newColumns.indexOf('Nombre')
    newColumns.splice(index + 1, 0, 'Correo')
    const data = {
      code: 'configurations/entity-table',
      parameters: {
        query: JSON.stringify(payload.value),
        fields: ['name', 'email', 'enabled', 'allowedRoles', 'allowedPayrolls'],
        headers: newColumns,
        module: t('users.list.title', 1)
      }
    }
    const id = await generateReportList(data)
    refresh.value = setInterval(async () => {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        descriptionModal.value = response.errors
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    }, 5000)
  } catch (error: any) {
    notificationError(error, 'users', t, notifications, loading)
  }
}

/* Cerrar el componente Modal */
const closeModal = () => {
  userUpdateState.value = []
  showModal.value = false
  showModalChangePassword.value = false
  resetForm({})
  urlFile.value = null
}

/* Listar usuarios */
const users = async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const data = await getUsers(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value,
      includeInactives.value
    )
    rows.value = data.data.data
    totalRows.value = data.data.totalItems
    payload.value = data.query
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'users', t, notifications, loading)
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="changeStateConfirm()">
    <template v-slot:description v-if="urlFile != null">
      <a
        class="underline tracking-widest text-blue-600"
        :href="urlFile"
        target="_blank"
        v-html="t('reports.notification.viewFile')" />
    </template>
  </DModal>
  <DModal
    size=""
    :form="true"
    :open="showModalChangePassword"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="onSubmit">
    <template #form>
      <div>
        <div class="mb-3">
          <Lock02Icon
            class="p-2.5 m-auto h-12 w-12 bg-blue-100 text-blue-600 rounded-full" />
          <p class="mt-4 mb-1.5 text-xl font-semibold text-center">
            {{ t('table.action.changePassword') }}
          </p>
        </div>
        <div
          v-if="wrongData"
          class="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4"
          role="alert">
          <p class="text-sm font-bold">{{ wrongData }}</p>
        </div>
        <form class="grid w-96 px-3">
          <div class="grid relative mb-3">
            <DTextField
              class-input="block-input"
              :label="t('form.newPassword.label')"
              :type="showNewPassword ? 'password' : 'text'"
              :error="errors.userNewPassword"
              :placeholder="t('form.newPassword.placeholder')"
              :required="true"
              v-model="userNewPassword" />
            <div class="absolute right-3 top-8 z-50">
              <EyeIcon
                class="text-gray-600"
                @click="showNewPassword = !showNewPassword"
                :class="{
                  hidden: !showNewPassword,
                  block: showNewPassword
                }" />
              <EyeOffIcon
                class="text-gray-600"
                @click="showNewPassword = !showNewPassword"
                :class="{
                  block: !showNewPassword,
                  hidden: showNewPassword
                }" />
            </div>
          </div>
          <div class="grid relative mb-3">
            <DTextField
              class-input="block-input"
              :label="t('form.confirmNewPassword.label')"
              :type="showConfirmNewPassword ? 'password' : 'text'"
              :error="errors.userConfirmNewPassword"
              :placeholder="t('form.confirmNewPassword.placeholder')"
              :required="true"
              v-model="userConfirmNewPassword" />
            <div class="absolute right-3 top-8 z-50">
              <EyeIcon
                class="text-gray-600"
                @click="showConfirmNewPassword = !showConfirmNewPassword"
                :class="{
                  hidden: !showConfirmNewPassword,
                  block: showConfirmNewPassword
                }" />
              <EyeOffIcon
                class="text-gray-600"
                @click="showConfirmNewPassword = !showConfirmNewPassword"
                :class="{
                  block: !showConfirmNewPassword,
                  hidden: showConfirmNewPassword
                }" />
            </div>
          </div>
          <div>
            <DBtn
              @click.prevent="getPassword"
              v-html="t('form.button.generatePassword')" />
          </div>
        </form>
      </div>
    </template>
  </DModal>
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('users.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('users.list.description')" />
      </div>
      <div class="sm:flex sm:justify-between items-center pt-8">
        <div class="grid grid-cols-12 md:grid-cols-12 items-end gap-3">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 col-span-6 sm:col-span-5 md:col-span-6"
            v-model="search"
            @keyup.enter="users" />
          <DToggleField
            v-model="includeInactives"
            @change="users"
            :label="t('users.list.includeInactives')"
            class="col-span-6 h-full sm:col-span-5 md:col-span-2 flex items-center" />
          <DBtn
            class="ml-4 h-10 col-span-10 md:col-span-2 lg:col-span-2"
            @click.prevent="users"
            v-html="t('form.button.search')" />
        </div>
        <div class="flex gap-2">
          <DBtn
            color="excel"
            type="button"
            @click="downloadList"
            v-html="t('form.button.download')" />
          <DBtn
            :icon="'PlusIcon'"
            type="button"
            @click="createUser()"
            v-if="store.hasPermission('user:create')">
            {{
              t('form.button.create', {
                form: t('users.list.title', 0)
              })
            }}
          </DBtn>
        </div>
      </div>
    </div>
    <DTable
      ref="tableUsers"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), users()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), users()">
      <template #nameUser="data: any">
        <div class="flex items-center">
          <div
            class="mr-3 mt-0.5 px-2 w-10 h-10 bg-blue-50 text-blue-500 rounded-full text-md flex justify-center items-center font-semibold">
            <p
              v-for="(name, index) in data.name.split(' ').slice(0, 2)"
              :key="index">
              {{ (name as string).charAt(0).toUpperCase() }}
            </p>
          </div>
          <div class="leading-5 mt-1">
            <p
              class="font-semibold overflow-hidden whitespace-nowrap text-ellipsis w-64">
              {{ data.name }}
            </p>
            <p
              class="text-sm overflow-hidden whitespace-nowrap text-ellipsis w-64">
              {{ data.email }}
            </p>
          </div>
        </div>
      </template>
      <template #state="data: any">
        <div
          class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
          :class="
            data.enabled
              ? 'bg-green-100 text-green-600'
              : 'bg-red-100 text-red-600'
          ">
          <div
            class="mr-2 w-1.5 h-1.5 rounded-full"
            :class="data.enabled ? 'bg-green-600' : 'bg-red-600'"></div>
          {{
            data.enabled ? t('table.action.active') : t('table.action.inactive')
          }}
        </div>
      </template>
      <template #rol="data: any">
        <div class="flex items-center text-gray-500">
          <div
            v-for="(roles, index) in data.allowedRoles.slice(0, 1)"
            :key="index">
            {{ (roles as any).name }}
          </div>
          <div
            v-if="data.allowedRoles.length > 1"
            class="bg-blue-50 rounded-lg text-blue-700 text-sm font-semibold p-0.5 ml-2">
            {{ data.allowedRoles.length - 1 }}+
          </div>
        </div>
      </template>
      <template #payroll="data: any">
        <div class="flex items-center text-gray-500">
          <div
            v-for="(payroll, index) in data.allowedPayrolls.slice(0, 1)"
            :key="index">
            {{ (payroll as any).code + ' - ' + (payroll as any).name }}
          </div>
          <div
            v-if="data.allowedPayrolls.length > 1"
            class="bg-blue-50 rounded-lg text-blue-700 text-sm font-semibold p-0.5 ml-2">
            {{ data.allowedPayrolls.length - 1 }}+
          </div>
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('user:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div class="bg-gray-50 rounded-md py-2" @click="getUser(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('user:update')"
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateUser(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('user:enable')"
            arrow
            offsetDistance="12"
            :content="
              data.enabled
                ? t('users.list.deactivate')
                : t('users.list.activate')
            "
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="changeStateUser(data)">
              <LockUnlocked03Icon
                v-if="data.enabled"
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
              <Lock03Icon
                v-else
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('user:update_password')"
            arrow
            offsetDistance="12"
            :content="t('table.action.changePassword')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="changePassword(data)">
              <LockKeyholeSquareIcon
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
